// Phase 86AZ.57 — ISG (Health & Safety) Ultra Admin view
// =====================================================
// Lists all contractors with their workers grouped. Click into a contractor
// to expand all workers; per-worker preview docs and Approve/Reject.
import React, { useEffect, useState } from 'react';
import {
  ChevronRight, ChevronDown, FileText, CheckCircle, XCircle, Clock,
  ShieldCheck, AlertCircle, Search, X, Download,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { DocLightbox } from './ContractorISG';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ status, tr }) => {
  const map = {
    pending:  { cls: 'bg-amber-100 text-amber-700',   icon: Clock,         label: tr ? 'Bekliyor'   : 'Pending' },
    approved: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: tr ? 'Onaylı'     : 'Approved' },
    rejected: { cls: 'bg-red-100 text-red-700',        icon: XCircle,      label: tr ? 'Reddedildi' : 'Rejected' },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

export default function UltraAdminISG({ tr }) {
  const [summary, setSummary] = useState([]);
  const [workersByCid, setWorkersByCid] = useState({});
  const [docsByWid, setDocsByWid] = useState({});
  const [expandedCid, setExpandedCid] = useState(null);
  const [expandedWid, setExpandedWid] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null); // { worker, reason }
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected
  const [search, setSearch] = useState('');

  const loadSummary = async () => {
    try {
      const r = await apiClient.get('/isg/admin/summary');
      setSummary(r.data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => {
    loadSummary();
  }, []);

  const loadWorkers = async (cid) => {
    try {
      const r = await apiClient.get(`/isg/admin/workers?contractor_user_id=${cid}`);
      setWorkersByCid(prev => ({ ...prev, [cid]: r.data || [] }));
    } catch (e) { console.error(e); }
  };

  const loadDocs = async (wid) => {
    try {
      const r = await apiClient.get(`/isg/workers/${wid}/documents`);
      setDocsByWid(prev => ({ ...prev, [wid]: r.data || [] }));
    } catch (e) { console.error(e); }
  };

  const toggleContractor = async (cid) => {
    if (expandedCid === cid) { setExpandedCid(null); return; }
    setExpandedCid(cid);
    if (!workersByCid[cid]) await loadWorkers(cid);
  };

  const toggleWorker = async (wid) => {
    if (expandedWid === wid) { setExpandedWid(null); return; }
    setExpandedWid(wid);
    if (!docsByWid[wid]) await loadDocs(wid);
  };

  const approve = async (worker) => {
    setBusyId(worker.id);
    try {
      await apiClient.post(`/isg/admin/workers/${worker.id}/approve`);
      await Promise.all([loadSummary(), loadWorkers(worker.contractor_user_id)]);
    } catch (e) { alert(e?.response?.data?.detail || 'failed'); }
    finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!confirmReject?.worker) return;
    const w = confirmReject.worker;
    setBusyId(w.id);
    try {
      await apiClient.post(`/isg/admin/workers/${w.id}/reject`, { reason: confirmReject.reason || '' });
      await Promise.all([loadSummary(), loadWorkers(w.contractor_user_id)]);
      setConfirmReject(null);
    } catch (e) { alert(e?.response?.data?.detail || 'failed'); }
    finally { setBusyId(null); }
  };

  const filterWorkers = (rows) => {
    let list = rows;
    if (filter !== 'all') list = list.filter(w => w.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(w =>
        (w.full_name || '').toLowerCase().includes(q) || (w.tc_no || '').includes(q)
      );
    }
    return list;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            {tr ? 'ISG — Yüklenici Personeli' : 'ISG — Contractor Personnel'}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {tr ? 'Her yüklenicinin sahaya çıkacak ekibini ve belgelerini inceleyip kişi bazlı onay verin.'
                : 'Review each contractor’s field crew and their documents, then approve per person.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={tr ? 'Ad veya TC ara…' : 'Search name or TC…'}
              data-testid="isg-admin-search"
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 w-56" />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            data-testid="isg-admin-filter"
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200">
            <option value="all">{tr ? 'Hepsi' : 'All'}</option>
            <option value="pending">{tr ? 'Bekleyen' : 'Pending'}</option>
            <option value="approved">{tr ? 'Onaylı' : 'Approved'}</option>
            <option value="rejected">{tr ? 'Reddedilen' : 'Rejected'}</option>
          </select>
        </div>
      </div>

      {summary.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{tr ? 'Henüz hiçbir yüklenici ISG personeli kaydetmemiş.' : 'No contractor has registered ISG personnel yet.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {summary.map(c => {
            const expanded = expandedCid === c.contractor_user_id;
            const allWorkers = workersByCid[c.contractor_user_id] || [];
            const workers = filterWorkers(allWorkers);
            return (
              <div key={c.contractor_user_id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button type="button"
                  onClick={() => toggleContractor(c.contractor_user_id)}
                  data-testid={`isg-admin-contractor-${c.contractor_user_id}`}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div className="text-left">
                      <p className="font-bold text-gray-800">{c.contractor_name}</p>
                      <p className="text-[11px] text-gray-400">
                        {c.total} {tr ? 'kişi' : 'people'} ·
                        {c.pending  ? ` ${c.pending} ${tr ? 'bekleyen'  : 'pending'} ·` : ''}
                        {c.approved ? ` ${c.approved} ${tr ? 'onaylı'   : 'approved'} ·` : ''}
                        {c.rejected ? ` ${c.rejected} ${tr ? 'reddedilen': 'rejected'}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {c.pending > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                        {c.pending} {tr ? 'inceleme' : 'review'}
                      </span>
                    )}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {workers.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-6">
                        {tr ? 'Bu filtreye uyan personel yok.' : 'No personnel matches this filter.'}
                      </p>
                    ) : workers.map(w => {
                      const wExpanded = expandedWid === w.id;
                      const docs = docsByWid[w.id] || [];
                      return (
                        <div key={w.id} data-testid={`isg-admin-worker-${w.id}`}>
                          <button type="button" onClick={() => toggleWorker(w.id)}
                            className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3 text-left min-w-0">
                              {wExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{w.full_name}</p>
                                <p className="text-[11px] text-gray-400 font-mono">
                                  TC {w.tc_no}{w.phone ? ` · ${w.phone}` : ''}{w.email ? ` · ${w.email}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-[10px] text-gray-400">{w.document_count} {tr ? 'belge' : 'docs'}</span>
                              <StatusPill status={w.status} tr={tr} />
                            </div>
                          </button>

                          {wExpanded && (
                            <div className="bg-gray-50/50 px-5 pt-3 pb-4 space-y-3">
                              {/* Documents */}
                              {docs.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">{tr ? 'Yüklenmiş belge yok.' : 'No documents uploaded.'}</p>
                              ) : (
                                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                                  {docs.map(d => {
                                    const url = `${API}/api/uploads/${d.filename}`;
                                    const isImg = /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(d.original_name || d.filename);
                                    const isPdf = /\.pdf$/i.test(d.original_name || d.filename);
                                    return (
                                      <button key={d.id} type="button"
                                        onClick={() => setLightbox({ url, name: d.original_name, ext: isImg ? 'img' : isPdf ? 'pdf' : 'other' })}
                                        data-testid={`isg-admin-doc-${d.id}`}
                                        className="aspect-square bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-emerald-300 hover:shadow-sm transition group">
                                        {isImg ? (
                                          <img src={url} alt={d.original_name} className="w-full h-full object-cover" />
                                        ) : (
                                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-1">
                                            <FileText className="w-6 h-6 mb-0.5" />
                                            <span className="text-[8px] font-mono uppercase">{isPdf ? 'PDF' : (d.original_name?.split('.').pop() || 'FILE')}</span>
                                            <span className="text-[7px] text-gray-500 truncate w-full text-center mt-0.5">{d.original_name}</span>
                                          </div>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {w.status === 'rejected' && w.rejected_reason && (
                                <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                                  <span className="font-bold">{tr ? 'Red gerekçesi: ' : 'Reject reason: '}</span>{w.rejected_reason}
                                </div>
                              )}

                              <div className="flex justify-end gap-2 pt-1">
                                <button type="button"
                                  onClick={() => setConfirmReject({ worker: w, reason: '' })}
                                  disabled={busyId === w.id}
                                  data-testid={`isg-admin-reject-${w.id}`}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 inline-flex items-center gap-1 disabled:opacity-40">
                                  <XCircle className="w-3.5 h-3.5" /> {tr ? 'Reddet' : 'Reject'}
                                </button>
                                <button type="button"
                                  onClick={() => approve(w)}
                                  disabled={busyId === w.id || docs.length === 0}
                                  title={docs.length === 0 ? (tr ? 'En az 1 belge gerekir' : 'At least 1 document required') : ''}
                                  data-testid={`isg-admin-approve-${w.id}`}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                                  <CheckCircle className="w-3.5 h-3.5" /> {tr ? 'Onayla' : 'Approve'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reject confirm modal */}
      {confirmReject && (
        <div className="fixed inset-0 z-[10005] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmReject(null); }}
          data-testid="isg-admin-reject-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-red-50 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <h3 className="font-bold text-base">{tr ? 'Personeli Reddet' : 'Reject Worker'}</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">
                <strong>{confirmReject.worker.full_name}</strong>
                <span className="text-gray-400 font-mono"> · TC {confirmReject.worker.tc_no}</span>
              </p>
              <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wide">
                {tr ? 'Red Gerekçesi (yükleniciye iletilecek)' : 'Reject Reason (sent to contractor)'}
              </label>
              <textarea
                data-testid="isg-admin-reject-reason"
                value={confirmReject.reason}
                onChange={e => setConfirmReject({ ...confirmReject, reason: e.target.value })}
                rows={4} maxLength={500}
                placeholder={tr ? 'Örn. Sağlık raporu güncel değil…' : 'e.g. Medical report is not up-to-date…'}
                className="w-full text-sm border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-red-200 focus:outline-none"
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button onClick={() => setConfirmReject(null)} data-testid="isg-admin-reject-cancel"
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button onClick={submitReject} disabled={busyId === confirmReject.worker.id} data-testid="isg-admin-reject-submit"
                className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5 disabled:opacity-40">
                <XCircle className="w-4 h-4" />
                {tr ? 'Reddet' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && <DocLightbox lightbox={lightbox} tr={tr} onClose={() => setLightbox(null)} />}
    </div>
  );
}
